import { useEffect, useRef, useState } from "react";
import tw from "tailwind-styled-components";
import { Formik, Field, Form } from "formik";
import { MdClose } from "react-icons/md";
import {
  FaCheckCircle,
  FaMoneyBillWave,
  FaExclamationCircle,
  FaRegCalendar,
} from "react-icons/fa";
import {
  InputGroup,
  FieldWrappers,
  Label,
  SubmitBtn,
  SideCancelBtn,
  Wrapper,
  ScrollableContent,
  ActionSection,
  RupeeIcon,
} from "../Styles/InputStyles";
import { toast } from "react-toastify";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import DropZone from "../Notice/Dropzone";
import { paymentModes } from "../../helpers";

const MarkAsPaidModal = ({
  setIsMarkAsPaidVisible,
  markAsPaidMutate,
  markAsPaidLoading,
  selectedData,
  isEvicted,
  bankAccount,
  securityRemaining
}) => {

  const [calendarOpen, setCalendarOpen] = useState(false);
  const formikRef = useRef(null);
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    const handleResize = () => {
      setHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const bankAccounts = Array.isArray(bankAccount) ? bankAccount : [];

  const initialValues = {
    id: selectedData?.id,
    ledgerReferenceId: selectedData?.ledgerReferenceId,
    collectionDate: "",
    paymentType: 0,
    amountPaid: selectedData.balance,
    paymentMode: "",
    transactionId: "",
    bankAccount: "",
    paymentAccountNo: "",
    paymentAccountName: "",
    file: null,
    remarks: "",
    useSecurityDeposit: 0,
    depositAdjusted: 0,
  };

  const paymentTypes = [
    {
      label: "Full Payment",
      value: 0,
      icon: <FaCheckCircle className="w-6 h-6 text-green-500" />,
    },
    {
      label: "Partial Payment",
      value: 1,
      icon: <FaMoneyBillWave className="w-6 h-6 text-blue-500" />,
    },
    // {
    //   label: "Excess Payment",
    //   value: 2,
    //   icon: <FaExclamationCircle className="w-6 h-6 text-red-500" />,
    // },
  ];

  const handleSubmit = (values, formikHelpers) => {
    if (!values.collectionDate) {
      toast.error("Collection Date is required");
      return;
    }

    if (values.paymentType === undefined || values.paymentType === null) {
      toast.error("Payment type is required");
      return;
    }

    if (
      values.paymentMode === undefined ||
      values.paymentMode === null ||
      !values.paymentMode
    ) {
      toast.error("Payment mode is required");
      return;
    }

    if (values.paymentType !== 0) {
      if (!values.amountPaid) {
        toast.error("Amount is required");
        return;
      }
      if (values.amountPaid <= 0) {
        toast.error("Amount must be positive");
        return;
      }
      if (
        values.paymentType !== 2 &&
        values.amountPaid > selectedData.balance
      ) {
        toast.error("Amount cannot exceed total dues for partial payment");
        return;
      }
    }

    if (values.useSecurityDeposit === 1) {
      if (values.depositAdjusted <= 0) {
        toast.error("Deposit adjustment amount must be positive");
        return;
      }

      if (values.depositAdjusted > securityRemaining) {
        toast.error("Deposit adjustment cannot exceed security remaining");
        return;
      }

      const actualPayment = values.paymentType === 0
        ? selectedData.balance - Number(values.depositAdjusted)
        : Number(values.amountPaid) - Number(values.depositAdjusted);

      if (actualPayment < 0) {
        toast.error("Deposit cannot exceed the payment amount");
        return;
      }
    }

    const amountToSend = values.paymentType === 0
      ? selectedData.balance - (values.useSecurityDeposit === 1 ? Number(values.depositAdjusted) : 0)
      : Number(values.amountPaid) - (values.useSecurityDeposit === 1 ? Number(values.depositAdjusted) : 0);

    if (amountToSend < 0) {
      toast.error("Payment amount cannot be negative");
      return;
    }

    let newTime = values.collectionDate + " " + moment().format("HH:mm:ss");

    const selectedBankAccount = [2, 3, 4].includes(values.paymentMode)
      ? bankAccounts.find(acc => acc.id.toString() === values.bankAccount)
      : null;

    const formData = new FormData()
    formData.append("collectionDate", moment(newTime).format("YYYY-MM-DD HH:mm:ss"))
    formData.append("transactionId", values.transactionId)
    formData.append("mode", values.paymentMode)
    formData.append("amount", amountToSend)
    formData.append("id", selectedData?.id)
    formData.append("ledgerReferenceId", selectedData?.ledgerReferenceId)
    formData.append("paymentType", values?.paymentType)
    formData.append("tenantId", selectedData?.tenantId)
    formData.append("isEvicted", isEvicted)
    formData.append("paymentAccountNo", selectedBankAccount?.accountNum || "",)
    formData.append("paymentAccountName", selectedBankAccount?.holderName || "");
    formData.append("remarks", values.remarks || "");
    formData.append("useSecurityDeposit", values.useSecurityDeposit);
    formData.append("depositAdjusted", values.depositAdjusted);
    if (values.file) {
      formData.append("file", values.file[0])
    }
    markAsPaidMutate(formData)
  };

  const handleClose = () => {
    setIsMarkAsPaidVisible(false);
  };

  const handleBankAccountChange = (event, setFieldValue) => {
    const selectedId = event.target.value;
    setFieldValue("bankAccount", selectedId);

    const selectedAccount = bankAccounts.find(acc => acc.id.toString() === selectedId);
    if (selectedAccount) {
      setFieldValue("paymentAccountNo", selectedAccount.accountNum);
      setFieldValue("paymentAccountName", selectedAccount.holderName);
    }
  };

  const clearBankAccountFields = (setFieldValue) => {
    setFieldValue("bankAccount", "");
    setFieldValue("paymentAccountNo", "");
    setFieldValue("paymentAccountName", "");
  };

  return (
    <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
      <Header>
        <h1 className="text-white text-lg font-medium">Mark as Paid</h1>
        <button onClick={handleClose}>
          <MdClose color="white" size={24} />
        </button>
      </Header>

      <div className="px-6 flex flex-col justify-between" >
        <Formik innerRef={formikRef} initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
          {({ setFieldValue, values, resetForm }) => {

            const shouldShowSecurityDeposit = () => {
              return (
                securityRemaining > 0 &&
                selectedData?.type !== 2 &&
                values.paymentType !== 2
              );
            };

            const calculateMaxDeposit = () => {
              if (values.paymentType === 0) {
                return Math.min(securityRemaining, selectedData.balance);
              } else if (values.paymentType === 1) {
                const partialAmount = Number(values.amountPaid || 0);
                return Math.min(securityRemaining, partialAmount);
              }
              return 0;
            };

            const calculateNetAmountDue = () => {
              if (values.useSecurityDeposit === 1) {
                if (values.paymentType === 0) {
                  return selectedData.balance - Number(values.depositAdjusted || 0);
                } else if (values.paymentType === 1) {
                  return Number(values.amountPaid || 0) - Number(values.depositAdjusted || 0);
                }
              } else {
                if (values.paymentType === 0) {
                  return selectedData.balance;
                } else if (values.paymentType === 1) {
                  return Number(values.amountPaid || 0);
                }
              }
              return 0;
            };

            const handleDepositChange = (depositAmount) => {
              const deposit = Number(depositAmount) || 0;

              if (deposit > securityRemaining) {
                toast.error(`Cannot exceed available deposit of ₹${securityRemaining}`);
                return;
              }

              const maxDeposit = calculateMaxDeposit();
              if (deposit > maxDeposit) {
                toast.error(`Maximum deposit allowed is ₹${maxDeposit}`);
                return;
              }

              setFieldValue("depositAdjusted", deposit);
            };

            return (
              <Form>
                <ScrollableContent className="!max-h-[calc(100dvh-140px)]">
                  <Wrapper>
                    <InputGroup>
                      <Label htmlFor="collectionDate">Collection Date *</Label>
                      <FieldWrappers className="relative w-full">
                        <DatePicker
                          selected={values.collectionDate ? new Date(values.collectionDate) : null}
                          onChange={(date) =>
                            setFieldValue("collectionDate", moment(date).format("YYYY-MM-DD"))
                          }
                          maxDate={new Date()}
                          dateFormat="yyyy-MM-dd"
                          className="  cursor-pointer"
                          placeholderText="YYYY-MM-DD"
                          onClickOutside={() => setCalendarOpen(false)}
                          onSelect={() => setCalendarOpen(false)}
                          open={calendarOpen}
                          onInputClick={() => setCalendarOpen(true)}
                          popperPlacement="top-end"
                          showIcon
                          icon={<FaRegCalendar className="text-gray-400 text-xs mt-0.5" />}
                          showPopperArrow={false}
                          onFocus={(e) => e.target.blur()}
                          wrapperClassName="w-full"
                          portalId="root-portal"
                          popperClassName="react-datepicker-popper"
                        />
                      </FieldWrappers>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="paymentType">Payment Type *</Label>
                      <div className="grid grid-cols-3 gap-2">
                        {paymentTypes.map((option) => (
                          <OptionCard
                            key={option.value}
                            isselected={(values.paymentType === option.value).toString()}
                            onClick={() => {
                              setFieldValue("paymentType", option.value);
                              if (option.value === 0) {
                                setFieldValue("amountPaid", selectedData.balance);
                              } else {
                                setFieldValue("amountPaid", "");
                              }
                              setFieldValue("useSecurityDeposit", 0);
                              setFieldValue("depositAdjusted", 0);
                              clearBankAccountFields(setFieldValue);
                            }}
                          >
                            <span className="text-sm">{option.label}</span>
                          </OptionCard>
                        ))}
                      </div>
                    </InputGroup>

                    {(values.paymentType === 0 || values.paymentType === 1 || values.paymentType === 2) && (
                      <InputGroup>
                        <Label htmlFor="amountPaid">
                          {values.paymentType === 0
                            ? "Amount"
                            : values.paymentType === 1
                              ? "Partial Amount"
                              : "Excess Payment"} *
                        </Label>
                        <FieldWrappers className="w-full">
                          <RupeeIcon />
                          <Field
                            name="amountPaid"
                            type="number"
                            disabled={values.paymentType === 0}
                            placeholder="Enter Amount"
                            className="w-full pl-5"
                            onChange={(e) => {
                              const amount = e.target.value;
                              setFieldValue("amountPaid", amount);
                              if (values.useSecurityDeposit === 1 && values.paymentType === 1) {
                                const deposit = Number(values.depositAdjusted || 0);
                                const newAmount = Number(amount || 0);
                                if (deposit > newAmount) {
                                  setFieldValue("depositAdjusted", 0);
                                }
                              }
                            }}
                          />
                        </FieldWrappers>
                        {values.useSecurityDeposit === 1 && values.paymentType === 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            This field is auto-calculated based on deposit adjustment
                          </div>
                        )}
                      </InputGroup>
                    )}

                    {/* Security Deposit Adjustment Section */}
                    {shouldShowSecurityDeposit() && (
                      <InputGroup>
                        <div className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg bg-gray-50">
                          <input
                            type="checkbox"
                            id="useSecurityDeposit"
                            checked={values.useSecurityDeposit === 1}
                            onChange={(e) => {
                              const isChecked = e.target.checked ? 1 : 0;
                              setFieldValue("useSecurityDeposit", isChecked);
                              if (!isChecked) {
                                setFieldValue("depositAdjusted", 0);
                              } else {
                                setFieldValue("depositAdjusted", 0);
                              }
                            }}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label htmlFor="useSecurityDeposit" className="text-sm text-gray-500">
                            Adjust using Security Deposit
                          </label>
                        </div>

                        {values.useSecurityDeposit === 1 && (
                          <div className="mt-4 p-4 border border-gray-200 rounded-lg bg-blue-50">
                            <div className=" flex justify-between">
                              <Label>Available Deposit</Label>
                              <div className="text-lg font-semibold text-gray-800">
                                ₹{securityRemaining.toLocaleString()}
                              </div>
                            </div>

                            <div className="mb-4">
                              <Label htmlFor="depositAdjusted">Deposit to Use</Label>
                              <FieldWrappers className="w-full">
                                <RupeeIcon />
                                <input
                                  type="number"
                                  value={values.depositAdjusted || ''}
                                  placeholder="Enter deposit amount to use"
                                  className="w-full pl-5 "
                                  min="0"
                                  max={calculateMaxDeposit()}
                                  onChange={(e) => handleDepositChange(e.target.value)}
                                  onWheel={(e) => e.target.blur()}
                                />
                              </FieldWrappers>
                            </div>

                            <div className="pt-3 border-t border-gray-200">
                              <Label>Net Amount Due (after deposit adjustment)</Label>
                              <div className="text-lg font-bold text-green-600">
                                ₹{calculateNetAmountDue()}
                              </div>
                            </div>
                          </div>
                        )}
                      </InputGroup>
                    )}

                    <InputGroup>
                      <Label>Payment Mode *</Label>
                      <PaymentModeWrapper>
                        {paymentModes.map((mode) => (
                          <PaymentModeCard
                            key={mode.value}
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              setFieldValue("paymentMode", mode.value, false);
                              clearBankAccountFields(setFieldValue);
                            }}
                            className={
                              values.paymentMode === mode.value
                                ? "bg-blue-100 border-blue-500"
                                : "bg-gray-100"
                            }
                          >
                            <span className="text-sm">{mode.label}</span>
                          </PaymentModeCard>
                        ))}
                      </PaymentModeWrapper>
                    </InputGroup>

                    {/* Only show bank account dropdown if there are bank accounts */}
                    {bankAccounts.length > 0 && [2, 3, 4].includes(values.paymentMode) && (
                      <InputGroup>
                        <Label htmlFor="bankAccount">Bank Account </Label>
                        <Select
                          id="bankAccount"
                          name="bankAccount"
                          value={values.bankAccount || ''}
                          onChange={(e) => handleBankAccountChange(e, setFieldValue)}
                          displayEmpty
                          fullWidth
                          className="px-2"
                          style={{ height: "46px" }}
                        >
                          <MenuItem value="">
                            <em>Select Bank Account</em>
                          </MenuItem>
                          {bankAccounts.map((account) => (
                            <MenuItem key={account.id} value={account.id.toString()}>
                              {account.accountNum} - {account.holderName}
                            </MenuItem>
                          ))}
                        </Select>
                      </InputGroup>
                    )}

                    <InputGroup>
                      <Label htmlFor="transactionId">Transaction ID</Label>
                      <FieldWrappers>
                        <Field
                          type="text"
                          name="transactionId"
                          id="transactionId"
                          autoComplete="off"
                        />
                      </FieldWrappers>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="remarks">Remarks</Label>
                      <FieldWrapper>
                        <Field
                          as={Textarea}
                          id="remarks"
                          name="remarks"
                          rows={4}
                          maxLength={500}
                          placeholder="Write your remark here..."
                        />
                      </FieldWrapper>
                    </InputGroup>

                    <InputGroup>
                      <Label htmlFor="file">Document(optional)</Label>
                      <FieldWrapper $select={true}>
                        <Field
                          required
                          name="file"
                          type="text"
                          id="file"
                          autoComplete="off"
                        >
                          {(props) => (
                            <DropZone
                              fields={props.field}
                              setFieldValue={props.form.setFieldValue}
                              componentFor="expense"
                              acceptedFiles="image/*,application/pdf"
                              File={props.field.value ? [props.field.value] : null}
                            />
                          )}
                        </Field>
                      </FieldWrapper>
                    </InputGroup>
                  </Wrapper>
                </ScrollableContent>
                <ActionSection>
                  <SideCancelBtn type="button" onClick={() => {
                    resetForm();
                    handleClose();
                  }}>
                    Cancel
                  </SideCancelBtn>
                  <SubmitBtn type="submit" disabled={markAsPaidLoading}>
                    {markAsPaidLoading ? "Submiting..." : "Submit"}
                  </SubmitBtn>
                </ActionSection>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Container>
  );
};

const PaymentModeWrapper = tw.div`grid grid-cols-4 gap-2 my-1.5`;
const PaymentModeCard = tw.button`flex flex-col items-center justify-center p-2 border rounded-lg shadow-sm cursor-pointer`;
const Container = tw.div`bg-white w-full h-full flex flex-col relative `;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const FieldWrapper = tw.div`w-full`
const OptionCard = tw.div`
  flex flex-col items-center justify-center p-3 rounded-lg shadow border border-gray-200 text-center
  cursor-pointer transition-all duration-200 
  ${(props) =>
    props.isselected === "true"
      ? "bg-blue-50 border-blue-500"
      : "bg-white hover:bg-gray-50"}
`;
const Textarea = tw.textarea`min-h-30 border border-gray-300 rounded pl-2 py-2 focus:ring-1 focus:ring-sky-600  focus:border-sky-600 focus:outline-none duration-200 w-full h-30 resize-y  break-words overflow-hidden`;

export default MarkAsPaidModal;